import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Feather, Check, Zap, BookOpen, Users, ChevronDown, Sparkles, Clock, Download } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { BrainstormIllustration, OutlineIllustration, WritingIllustration } from '@/assets/illustrations';
import { Link } from 'react-router-dom';
import { useAuth, SignUpButton } from '@clerk/clerk-react';
import { getDomainConfig } from '@/lib/domain-config';

const FADE_IN_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const HeroSection = () => {
  const { isSignedIn } = useAuth();
  const domainConfig = getDomainConfig();

  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={{
        visible: { transition: { staggerChildren: 0.2 } },
      }}
      className="text-center pt-32 pb-16 md:pt-48 md:pb-24"
    >
      <motion.div variants={FADE_IN_VARIANTS} className="inline-flex items-center gap-2 bg-brand-accent/10 dark:bg-neutral-800/30 px-4 py-2 rounded-full mb-8">
        <Sparkles className="w-4 h-4 text-brand-accent" />
        <span className="text-sm font-medium text-brand-dark dark:text-amber-50">Powered by Claude 4.6 Sonnet</span>
      </motion.div>

      <motion.h1
        variants={FADE_IN_VARIANTS}
        className="font-serif text-5xl md:text-7xl font-bold text-brand-dark dark:text-amber-50 tracking-tight leading-tight"
      >
        {domainConfig.h1Title}
        <br />
        <span className="text-brand-accent">{domainConfig.h1Subtitle}</span>
      </motion.h1>

      <motion.p
        variants={FADE_IN_VARIANTS}
        className="mt-6 max-w-3xl mx-auto text-lg md:text-xl text-brand-dark/80 dark:text-amber-100/80 leading-relaxed"
      >
        Transform a single idea into a complete 20,000-word novel with our free AI {domainConfig.generatorType}. Our AI {domainConfig.writerType} creates a unique author persona, plots your narrative backwards for perfect structure, and generates your entire {domainConfig.contentType} in one uninterrupted stream.
      </motion.p>

      <motion.div variants={FADE_IN_VARIANTS} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
        {isSignedIn ? (
          <Button asChild size="lg" className="bg-brand-dark text-brand-parchment hover:bg-brand-dark/90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <Link to="/app/workspace">
              Start Writing for Free
              <Feather className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        ) : (
          <SignUpButton mode="modal" forceRedirectUrl="/app/workspace">
            <Button size="lg" className="bg-brand-dark text-brand-parchment hover:bg-brand-dark/90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              Start Writing for Free
              <Feather className="ml-2 h-5 w-5" />
            </Button>
          </SignUpButton>
        )}
        <Button asChild variant="outline" size="lg" className="border-brand-dark text-brand-dark dark:text-amber-50 dark:border-amber-100 hover:bg-brand-accent/10 dark:bg-neutral-800/30 dark:hover:bg-neutral-700/30 rounded-full px-8 py-6 text-lg font-semibold">
          <a href="#how-it-works">
            See How It Works
          </a>
        </Button>
      </motion.div>

    <motion.div variants={FADE_IN_VARIANTS} className="mt-12 flex items-center justify-center gap-8 text-sm text-brand-dark/60 dark:text-amber-100/60">
      <div className="flex items-center gap-2">
        <Check className="w-4 h-4 text-green-600" />
        <span>No credit card required</span>
      </div>
      <div className="flex items-center gap-2">
        <Check className="w-4 h-4 text-green-600" />
        <span>2 free {domainConfig.contentTypePlural}</span>
      </div>
      <div className="hidden sm:flex items-center gap-2">
        <Check className="w-4 h-4 text-green-600" />
        <span>Export as EPUB</span>
      </div>
    </motion.div>
  </motion.section>
  );
};

const SocialProofSection = () => {
  const domainConfig = getDomainConfig();

  return (
    <section className="py-16 border-y border-brand-accent/20 dark:border-neutral-700 bg-gradient-to-r from-brand-accent/5 to-transparent">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="font-serif text-4xl md:text-5xl font-bold text-brand-dark dark:text-amber-50">20K</div>
          <div className="mt-2 text-sm md:text-base text-brand-dark/70 dark:text-amber-100/70">Words Per {domainConfig.contentTypeCapitalized}</div>
        </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="font-serif text-4xl md:text-5xl font-bold text-brand-dark dark:text-amber-50">10-15</div>
        <div className="mt-2 text-sm md:text-base text-brand-dark/70 dark:text-amber-100/70">Minutes to Generate</div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="font-serif text-4xl md:text-5xl font-bold text-brand-dark dark:text-amber-50">100%</div>
        <div className="mt-2 text-sm md:text-base text-brand-dark/70 dark:text-amber-100/70">Original {domainConfig.contentTypeCapitalized}s</div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <div className="font-serif text-4xl md:text-5xl font-bold text-brand-dark dark:text-amber-50">64K</div>
        <div className="mt-2 text-sm md:text-base text-brand-dark/70 dark:text-amber-100/70">AI Output Tokens</div>
      </motion.div>
    </div>
  </section>
  );
};

const FeatureSection = () => {
  const domainConfig = getDomainConfig();

  return (
  <section id="how-it-works" className="py-24 md:py-32">
    <div className="text-center mb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="inline-flex items-center gap-2 bg-brand-accent/10 dark:bg-neutral-800/30 px-4 py-2 rounded-full mb-4"
      >
        <Zap className="w-4 h-4 text-brand-accent" />
        <span className="text-sm font-medium text-brand-dark dark:text-amber-50">The NovellaStream Process</span>
      </motion.div>
      <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark dark:text-amber-50">
        How Our AI {domainConfig.contentTypeCapitalized} Generator Works
      </h2>
      <p className="mt-4 max-w-2xl mx-auto text-lg text-brand-dark/80 dark:text-amber-100/80">
        Unlike simple AI writing tools, our {domainConfig.generatorType} uses a sophisticated 3-step process to create novels with depth, structure, and authentic voice.
      </p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="relative text-center p-8 bg-white/50 dark:bg-neutral-800/50 rounded-2xl border-2 border-brand-accent/20 dark:border-neutral-700 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
      >
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-brand-accent text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">1</div>
        <BrainstormIllustration className="w-20 h-20 mx-auto text-brand-accent" />
        <h3 className="mt-6 font-serif text-2xl font-semibold text-brand-dark dark:text-amber-50">AI Creates Unique Author Personas</h3>
        <p className="mt-3 text-brand-dark/80 dark:text-amber-100/80 leading-relaxed">
          Our AI {domainConfig.generatorType} crafts a unique authorial voice with forensic detail—memories, influences, sentence rhythms, and stylistic quirks—giving your generated {domainConfig.contentType} a distinct, consistent personality.
        </p>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative text-center p-8 bg-white/50 dark:bg-neutral-800/50 rounded-2xl border-2 border-brand-accent/20 dark:border-neutral-700 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
      >
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-brand-accent text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">2</div>
        <OutlineIllustration className="w-20 h-20 mx-auto text-brand-accent" />
        <h3 className="mt-6 font-serif text-2xl font-semibold text-brand-dark dark:text-amber-50">AI {domainConfig.contentTypeCapitalized} Plot Structuring</h3>
        <p className="mt-3 text-brand-dark/80 dark:text-amber-100/80 leading-relaxed">
          Our AI {domainConfig.generatorType} plots from ending to beginning, ensuring every scene has purpose, every thread connects, and your {domainConfig.contentType} climax feels earned—not forced.
        </p>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="relative text-center p-8 bg-white/50 dark:bg-neutral-800/50 rounded-2xl border-2 border-brand-accent/20 dark:border-neutral-700 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
      >
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-brand-accent text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">3</div>
        <WritingIllustration className="w-20 h-20 mx-auto text-brand-accent" />
        <h3 className="mt-6 font-serif text-2xl font-semibold text-brand-dark dark:text-amber-50">Generate Complete {domainConfig.contentTypeCapitalized}s Instantly</h3>
        <p className="mt-3 text-brand-dark/80 dark:text-amber-100/80 leading-relaxed">
          Watch the AI write your complete {domainConfig.contentType} in real-time—up to 20,000 words—streamed in one uninterrupted flow with perfect narrative coherence.
        </p>
      </motion.div>
    </div>
  </section>
  );
};

const BenefitsSection = () => {
  const domainConfig = getDomainConfig();

  return (
  <section className="py-24 md:py-32 bg-gradient-to-b from-brand-accent/5 to-transparent rounded-3xl">
    <div className="max-w-5xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark dark:text-amber-50">
          Why Choose This AI {domainConfig.contentTypeCapitalized} Writer?
        </h2>
        <p className="mt-4 text-lg text-brand-dark/70 dark:text-amber-100/70">
          The fastest AI {domainConfig.generatorType} for turning concepts into complete manuscripts
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[
          { icon: Clock, title: 'Skip the Blank Page', description: 'No more staring at an empty document. Generate a complete first draft in minutes, then edit to perfection.' },
          { icon: BookOpen, title: 'Professional Quality', description: 'Sophisticated prose with proper story structure, character development, and thematic depth—not generic AI slop.' },
          { icon: Download, title: 'Instant EPUB Export', description: 'Download publication-ready EPUB files to read on Kindle, Apple Books, or any e-reader.' },
          { icon: Users, title: 'Unique Every Time', description: `Each ${domainConfig.contentType} uses a custom-generated author persona—no two ${domainConfig.contentTypePlural} sound alike, even with similar prompts.` },
        ].map((benefit, index) => (
          <motion.div
            key={benefit.title}
            initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card className="h-full bg-white/70 dark:bg-neutral-800/70 border-brand-accent/20 dark:border-neutral-700 hover:border-brand-accent/40 transition-all">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-brand-accent/10 dark:bg-neutral-800/30 rounded-lg">
                    <benefit.icon className="w-6 h-6 text-brand-accent" />
                  </div>
                  <div>
                    <CardTitle className="font-serif text-xl text-brand-dark dark:text-amber-50">{benefit.title}</CardTitle>
                    <CardDescription className="mt-2 text-brand-dark/70 dark:text-amber-100/70 leading-relaxed">
                      {benefit.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};

const PricingSection = () => {
  const domainConfig = getDomainConfig();
  const { isSignedIn } = useAuth();

  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: `Try our AI ${domainConfig.generatorType}`,
      features: [
        `2 AI-generated ${domainConfig.contentTypePlural} per month`,
        `Full 20,000-word ${domainConfig.contentTypePlural}`,
        `Public ${domainConfig.contentType} sharing`,
        'Web-only viewing',
      ],
      cta: isSignedIn ? 'Go to Workspace' : 'Start Free',
      ctaLink: '/app/workspace',
      popular: false,
      isFree: true,
    },
    {
      name: 'Basic',
      price: '$12.99',
      period: 'per month',
      description: 'For hobbyists and aspiring authors',
      features: [
        `7 AI ${domainConfig.contentTypePlural} per month`,
        `Full 20,000-word ${domainConfig.contentTypePlural}`,
        'EPUB export included',
        'Full commercial rights',
        'Unlimited storage',
      ],
      cta: 'Subscribe to Basic',
      ctaLink: '/pricing',
      popular: true,
      isFree: false,
    },
    {
      name: 'Author',
      price: '$29.99',
      period: 'per month',
      description: 'For serious writers and creators',
      features: [
        `15 AI-generated ${domainConfig.contentTypePlural} per month`,
        `Full 20,000-word ${domainConfig.contentTypePlural}`,
        'EPUB export included',
        'Full commercial rights',
        'Unlimited storage',
        'Priority support',
      ],
      cta: 'Subscribe to Author',
      ctaLink: '/pricing',
      popular: false,
      isFree: false,
    },
  ];

  return (
    <section id="pricing" className="py-24 md:py-32">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark dark:text-amber-50">
          Free AI {domainConfig.contentTypeCapitalized} Generator Pricing
        </h2>
        <p className="mt-4 text-lg text-brand-dark/70 dark:text-amber-100/70">
          Start free with 2 AI-generated {domainConfig.contentTypePlural} per month, upgrade for unlimited storytelling
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {plans.map((plan, index) => (
          <motion.div
            key={plan.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="relative"
          >
            {plan.popular && (
              <div className="absolute -top-4 left-0 right-0 flex justify-center">
                <span className="bg-brand-accent text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </span>
              </div>
            )}
            <Card className={`h-full ${plan.popular ? 'border-brand-accent border-2 shadow-xl scale-105' : 'border-brand-accent/20 dark:border-neutral-700'} bg-white/70 dark:bg-neutral-800/70 hover:shadow-xl transition-all`}>
              <CardHeader className="text-center pb-8">
                <CardTitle className="font-serif text-2xl text-brand-dark dark:text-amber-50">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="font-serif text-5xl font-bold text-brand-dark dark:text-amber-50">{plan.price}</span>
                  <span className="text-brand-dark/60 dark:text-amber-100/60 ml-2">{plan.period}</span>
                </div>
                <CardDescription className="mt-3 text-brand-dark/70 dark:text-amber-100/70">{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" />
                      <span className="text-brand-dark/80 dark:text-amber-100/80">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild className={`w-full rounded-full py-6 ${plan.popular ? 'bg-brand-accent hover:bg-brand-accent/90' : 'bg-brand-dark hover:bg-brand-dark/90'} text-white font-semibold`}>
                  <Link to={plan.ctaLink}>
                    {plan.cta}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const domainConfig = getDomainConfig();

  const testimonials = [
    {
      quote: "I've been stuck on a novel outline for months. NovellaStream gave me a complete first draft in 3 minutes. I couldn't believe the quality—it reads like a real author wrote it.",
      author: "Sarah Chen",
      role: "Indie Author",
    },
    {
      quote: "The reverse outlining technique is genius. Every plot point connects perfectly. I use this to prototype story ideas before committing to longer projects.",
      author: "Marcus Rodriguez",
      role: "Writing Coach",
    },
    {
      quote: "As someone who struggled with writer's block, this tool changed everything. The AI persona feature creates voices that feel genuinely distinct and human.",
      author: "Emily Hartwell",
      role: "Creative Writing Student",
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-brand-accent/5 dark:bg-neutral-800/20 rounded-3xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark dark:text-amber-50">
          What Writers Say About Our AI {domainConfig.contentTypeCapitalized} Generator
        </h2>
        <p className="mt-4 text-lg text-brand-dark/70 dark:text-amber-100/70">
          Join thousands of authors using AI to generate {domainConfig.contentTypePlural} and complete novels
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {testimonials.map((testimonial, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card className="h-full bg-white/70 dark:bg-neutral-800/70 border-brand-accent/20 dark:border-neutral-700 hover:border-brand-accent/40 transition-all">
              <CardContent className="pt-6">
                <div className="text-6xl text-brand-accent/20 font-serif leading-none mb-4">"</div>
                <p className="text-brand-dark/80 dark:text-amber-100/80 italic leading-relaxed">
                  {testimonial.quote}
                </p>
                <div className="mt-6 pt-4 border-t border-brand-accent/10 dark:border-neutral-700">
                  <p className="font-semibold text-brand-dark dark:text-amber-50">{testimonial.author}</p>
                  <p className="text-sm text-brand-dark/60 dark:text-amber-100/60">{testimonial.role}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const FAQSection = () => {
  const domainConfig = getDomainConfig();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: `How long does it take to generate a ${domainConfig.contentType}?`,
      answer: `Most ${domainConfig.contentTypePlural} complete in 10-15 minutes. Our AI writes up to 20,000 words in one continuous stream using Cloudflare's distributed architecture.`
    },
    {
      question: `Can I edit the generated ${domainConfig.contentType}?`,
      answer: "Absolutely! Download as EPUB or copy the text to edit in your favorite writing app. Think of it as a sophisticated first draft that you can refine."
    },
    {
      question: "What makes NovellaStream different from ChatGPT?",
      answer: "We use a specialized 3-step agentic process: creating a unique author persona, reverse-engineering plot structure, and streaming the complete story. Generic chatbots can't match this narrative depth."
    },
    {
      question: `Do I own the rights to the ${domainConfig.contentTypePlural}?`,
      answer: `Yes! You retain full copyright and commercial rights to every ${domainConfig.contentType} generated. Use them for publication, adaptation, or any purpose you choose.`
    },
    {
      question: "What genres work best?",
      answer: "All genres work: literary fiction, sci-fi, romance, mystery, fantasy, horror, historical, and everything in between. The AI adapts its voice to match your genre."
    },
    {
      question: "How does the free plan work?",
      answer: `Get 2 complete ${domainConfig.contentTypePlural} per month at no cost. No credit card required. Each ${domainConfig.contentType} is full-length (up to 20k words) with EPUB export included.`
    },
  ];

  return (
    <section className="py-24 md:py-32">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark dark:text-amber-50">
          AI {domainConfig.contentTypeCapitalized} Generator FAQ
        </h2>
        <p className="mt-4 text-lg text-brand-dark/70 dark:text-amber-100/70">
          Everything you need to know about generating {domainConfig.contentTypePlural} with AI
        </p>
      </motion.div>

      <div className="max-w-3xl mx-auto space-y-4">
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.05 }}
          >
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              className="w-full text-left p-6 bg-white/70 dark:bg-neutral-800/70 hover:bg-white/90 dark:hover:bg-neutral-800/90 rounded-xl border border-brand-accent/20 dark:border-neutral-700 hover:border-brand-accent/40 transition-all"
            >
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-serif text-lg md:text-xl font-semibold text-brand-dark dark:text-amber-50 pr-8">
                  {faq.question}
                </h3>
                <ChevronDown
                  className={`w-5 h-5 text-brand-accent flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </div>
              {openIndex === index && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 text-brand-dark/70 dark:text-amber-100/70 leading-relaxed"
                >
                  {faq.answer}
                </motion.div>
              )}
            </button>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const FinalCTASection = () => {
  const domainConfig = getDomainConfig();
  const { isSignedIn } = useAuth();

  return (
    <section className="py-24 md:py-32">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        className="relative overflow-hidden bg-gradient-to-br from-brand-dark to-brand-accent/90 rounded-3xl p-12 md:p-16 text-center shadow-2xl"
      >
        <div className="absolute inset-0 bg-[url('/noise.png')] opacity-5"></div>
        <div className="relative z-10">
          <h2 className="font-serif text-4xl md:text-6xl font-bold text-brand-parchment">
            Start Using Our AI {domainConfig.contentTypeCapitalized} Generator Free
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-lg md:text-xl text-brand-parchment/90">
            Join thousands of writers using AI to generate complete {domainConfig.contentTypePlural} and novels instantly.
            No credit card required—start writing today.
          </p>
          <div className="mt-10 px-4">
            {isSignedIn ? (
              <Button asChild size="lg" className="bg-brand-parchment text-brand-dark hover:bg-brand-parchment/90 rounded-full px-6 sm:px-10 py-6 sm:py-7 text-base sm:text-lg font-bold shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto">
                <Link to="/app/workspace" className="flex items-center justify-center">
                  <span className="truncate">Generate Your First {domainConfig.contentTypeCapitalized} Free</span>
                  <Feather className="ml-2 h-5 w-5 flex-shrink-0" />
                </Link>
              </Button>
            ) : (
              <SignUpButton mode="modal" forceRedirectUrl="/app/workspace">
                <Button size="lg" className="bg-brand-parchment text-brand-dark hover:bg-brand-parchment/90 rounded-full px-6 sm:px-10 py-6 sm:py-7 text-base sm:text-lg font-bold shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto flex items-center justify-center">
                  <span className="truncate">Generate Your First {domainConfig.contentTypeCapitalized} Free</span>
                  <Feather className="ml-2 h-5 w-5 flex-shrink-0" />
                </Button>
              </SignUpButton>
            )}
          </div>
          <p className="mt-6 text-sm text-brand-parchment/70">
            Start with 2 free {domainConfig.contentTypePlural}. Cancel anytime.
          </p>
        </div>
      </motion.div>
    </section>
  );
};

export function HomePage() {
  return (
    <div className="bg-brand-parchment dark:bg-neutral-900 text-brand-dark dark:text-amber-50 font-sans transition-colors duration-300">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-0">
          <HeroSection />
          <SocialProofSection />
          <FeatureSection />
          <BenefitsSection />
          <PricingSection />
          <TestimonialsSection />
          <FAQSection />
          <FinalCTASection />
        </div>
      </main>
      <Footer />
    </div>
  );
}
